// src/pages/accounting/SalaryMatrixPage.jsx
// Extracted from App.js - Salary Matrix admin page
// BRIO Migration: Using code "5a" (table dense + striped)
//
// CURRENCY CONVERSION (display-only):
// ────────────────────────────────────
// All stored values remain in baseCurrency (INR).
// Display conversion is handled by useSettings():
//   - formatMoneyInDisplayCurrency(amount, sourceCurrency, date)
//   - baseCurrency / displayCurrency / getFxRateForDate()
// The user toggles displayCurrency via the forex settings modal in App.js.
//
import React, { useState } from 'react';
import { Edit2, X, ChevronUp, ChevronDown, Pencil, Trash2 } from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioByCode,
  BrioTable,
  getTablePropsFromCode,
  useFullScreenPage,
  BrioFullScreenButton
} from '../../components/BrioComponents';
import { useSettings } from '../../hooks/useSettings';

// Presentation code for this page
const PRESENTATION_CODE = '5a';

// ---------------------------------------------------------------------------
// Styles for the history section (follows RH_STYLES pattern from RoleHistoryEditor)
// ---------------------------------------------------------------------------
const MH_STYLES = {
  table: {
    width: '100%',
    fontSize: '12px',
    borderCollapse: 'collapse',
    marginTop: '4px',
  },
  th: {
    padding: '6px 8px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '11px',
    color: 'var(--brio-muted, #64748b)',
    borderBottom: '2px solid var(--brio-border, #e2e8f0)',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '5px 8px',
    borderBottom: '1px solid var(--brio-border, #f1f5f9)',
    verticalAlign: 'middle',
    fontSize: '12px',
  },
  badge: (color) => ({
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 500,
    background: color === 'green' ? '#dcfce7' : color === 'amber' ? '#fef3c7' : '#f1f5f9',
    color: color === 'green' ? '#166534' : color === 'amber' ? '#92400e' : '#475569',
  }),
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    marginTop: '8px',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  formLabel: {
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--brio-muted, #64748b)',
  },
  inputStyle: {
    padding: '6px 8px',
    border: '1px solid var(--brio-border, #d1d5db)',
    borderRadius: '4px',
    fontSize: '12px',
    width: '100%',
    boxSizing: 'border-box',
  },
};

export default function SalaryMatrixPage({
  // State
  salaryMatrix,
  setSalaryMatrix,
  salaryMatrixHistory,
  setSalaryMatrixHistory,
  isEditingMatrix,
  setIsEditingMatrix,
  tempSalaryMatrix,
  setTempSalaryMatrix,
  form,
  setForm,
  currentUser,
  jobRoles,
  // Supabase
  useSupabaseDB,
  supabaseSalaryMatrix,
  // DEPRECATED — kept for backward compat only; useSettings() is the source of truth
  currSymbol: _currSymbol,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);

  // ---------------------------------------------------------------------------
  // History section state
  // ---------------------------------------------------------------------------
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [editingRevision, setEditingRevision] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editFormError, setEditFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Find the matrix revision matching today's date, or fall back to latest
  const findCurrentPeriodMatrix = (historyArr) => {
    const today = new Date().toISOString().split('T')[0];
    const match = historyArr.find(h => h.effectiveDate <= today && (!h.endDate || h.endDate >= today));
    return (match || historyArr[0])?.matrix;
  };

  const resetEditForm = () => {
    setShowEditForm(false);
    setEditingRevision(null);
    setEditFormData({});
    setEditFormError(null);
  };

  const openRevisionEditForm = (revision) => {
    setEditingRevision(revision);
    setEditFormData({
      effectiveDate: revision.effectiveDate || '',
      endDate: revision.endDate || '',
      changeReason: revision.changeReason || '',
      matrix: revision.matrix ? JSON.parse(JSON.stringify(revision.matrix)) : null,
    });
    setEditFormError(null);
    setShowEditForm(true);
  };

  const handleRevisionSave = async () => {
    if (!editFormData.effectiveDate) {
      setEditFormError('Effective Date is required.');
      return;
    }

    setSaving(true);
    setEditFormError(null);

    const payload = {
      effectiveDate: editFormData.effectiveDate,
      endDate: editFormData.endDate || null,
      changedBy: currentUser.email,
      changeReason: editFormData.changeReason || 'Matrix update',
      matrix: editFormData.matrix || undefined,
    };

    if (useSupabaseDB) {
      const result = await supabaseSalaryMatrix.updateSalaryMatrixRevision(editingRevision.id, payload);
      setSaving(false);
      if (result.success) {
        setSalaryMatrixHistory(result.history);
        const currentMatrix = findCurrentPeriodMatrix(result.history);
        if (currentMatrix) setSalaryMatrix(currentMatrix);
        resetEditForm();
      } else {
        setEditFormError(result.error || 'Update failed.');
      }
    } else {
      const updated = salaryMatrixHistory.map(h =>
        h.id === editingRevision.id
          ? { ...h, effectiveDate: payload.effectiveDate, endDate: payload.endDate, changedBy: payload.changedBy, changeReason: payload.changeReason, ...(payload.matrix ? { matrix: payload.matrix } : {}) }
          : h
      );
      setSalaryMatrixHistory(updated);
      setSaving(false);
      resetEditForm();
    }
  };

  const handleRevisionDelete = async (id) => {
    setSaving(true);

    if (useSupabaseDB) {
      const result = await supabaseSalaryMatrix.deleteSalaryMatrixRevision(id);
      setSaving(false);
      setConfirmDeleteId(null);
      if (result.success) {
        setSalaryMatrixHistory(result.history);
        const currentMatrix = findCurrentPeriodMatrix(result.history);
        if (currentMatrix) setSalaryMatrix(currentMatrix);
      } else {
        alert('Delete failed: ' + (result.error || 'Unknown error'));
      }
    } else {
      const filtered = salaryMatrixHistory.filter(h => h.id !== id);
      setSalaryMatrixHistory(filtered);
      setSaving(false);
      setConfirmDeleteId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // CURRENCY: useSettings provides all FX conversion + formatting
  // ---------------------------------------------------------------------------
  const {
    baseCurrency,
    displayCurrency,
    formatMoneyInDisplayCurrency,
    formatMoney,
    getFxRateForDate,
  } = useSettings();

  const needsConversion = displayCurrency !== baseCurrency;

  // Stable FX date anchor for salary matrix values (annual)
  const salaryFxDate = `${new Date().getFullYear()}-01-01`;

  /**
   * Format a base-currency salary amount for display.
   * Converts INR → displayCurrency using the FX rate for salaryFxDate,
   * then formats with the correct symbol and locale.
   * Display-only: stored values are NEVER mutated.
   */
  const money = (value) => {
    return formatMoneyInDisplayCurrency(value || 0, baseCurrency, salaryFxDate);
  };

  const { isFullScreen, toggle: toggleFullScreen, rootStyle, rootClassName } = useFullScreenPage(true);

  return (
    <div className={`brio-flex brio-flex-col brio-analytics-font ${rootClassName}`} style={{ background: '#fff', ...rootStyle }}>
      {/* PAGE HEADER BAND */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

              <p className="brio-analytics-muted brio-mt-xs">Monthly salary by job role and level (0-20). Markup is calculated office-wide in Accounting.</p>
            </div>
            <div className="brio-page-header__right">
              {!isEditingMatrix ? (
                <button 
                  onClick={() => {
                    setIsEditingMatrix(true);
                    setTempSalaryMatrix(JSON.parse(JSON.stringify(salaryMatrix)));
                  }}
                  className="brio-btn brio-btn-primary brio-flex brio-items-center brio-gap-xs"
                >
                  <Edit2 className="w-4 h-4"/>
                  Edit Matrix
                </button>
              ) : (
                <div className="brio-flex brio-flex-col brio-gap-s">
                  <div className="brio-flex brio-items-center" style={{ flexWrap: 'wrap', gap: '6px' }}>
                    <div>
                      <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '2px' }}>Start Date</label>
                      <input
                        type="date"
                        value={form.matrixEffectiveDate || new Date().toISOString().split('T')[0]}
                        onChange={(e) => setForm({...form, matrixEffectiveDate: e.target.value})}
                        style={{ fontFamily: 'Raleway, sans-serif', fontSize: '10px', padding: '3px 4px', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 0, background: 'var(--brio-white, #fff)', width: '100px', WebkitAppearance: 'none', appearance: 'none' }}
                      />
                    </div>
                    <div>
                      <label className="brio-text-xs brio-font-medium" style={{ display: 'block', marginBottom: '2px' }}>End Date <span style={{ fontWeight: 300 }}>(opt.)</span></label>
                      <input
                        type="date"
                        value={form.matrixEndDate || ''}
                        onChange={(e) => setForm({...form, matrixEndDate: e.target.value})}
                        style={{ fontFamily: 'Raleway, sans-serif', fontSize: '10px', padding: '3px 4px', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 0, background: 'var(--brio-white, #fff)', width: '100px', WebkitAppearance: 'none', appearance: 'none' }}
                      />
                    </div>
                    <div>
                      <label className="brio-text-xs brio-font-medium brio-mb-xs" style={{ display: 'block' }}>Change Reason (optional)</label>
                      <input
                        type="text"
                        value={form.matrixChangeReason || ''}
                        onChange={(e) => setForm({...form, matrixChangeReason: e.target.value})}
                        placeholder="e.g., Annual revision"
                        className="brio-btn brio-btn-secondary"
                        style={{ padding: '8px 12px' }}
                      />
                    </div>
                  </div>
                  
                  <div style={{ background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold)', padding: '12px' }}>
                    <p className="brio-text-xs" style={{ color: 'var(--brio-gold-hover)' }}>
                      💡 New matrix version will be created. Salary calculations for months within the start/end date range will use this matrix. Leave end date empty for an open-ended version.
                    </p>
                  </div>
                  
                  <div className="brio-flex brio-gap-xs">
                    <button 
                      onClick={async () => {
                        const effectiveDate = form.matrixEffectiveDate || new Date().toISOString().split('T')[0];
                        
                        const newMatrixRevision = {
                          id: Date.now(),
                          effectiveDate: effectiveDate,
                          endDate: form.matrixEndDate || null,
                          changedBy: currentUser.email,
                          changeReason: form.matrixChangeReason || 'Matrix update',
                          matrix: JSON.parse(JSON.stringify(tempSalaryMatrix))
                        };
                        
                        if (useSupabaseDB) {
                          const result = await supabaseSalaryMatrix.createSalaryMatrixRevision(newMatrixRevision);
                          if (result.success) {
                            setSalaryMatrixHistory(result.history);
                            setSalaryMatrix(tempSalaryMatrix);
                            console.log('✅ Salary matrix saved to Supabase');
                          } else {
                            alert(`Error saving salary matrix: ${result.error}`);
                            return;
                          }
                        } else {
                          setSalaryMatrixHistory([...salaryMatrixHistory, newMatrixRevision]);
                          setSalaryMatrix(tempSalaryMatrix);
                        }
                        
                        setIsEditingMatrix(false);
                        setTempSalaryMatrix(null);
                        setForm({});
                        
                        alert(`✓ Salary matrix updated!\nEffective from: ${effectiveDate}\nCost calculations will use the new matrix from this date onwards.`);
                      }}
                      className="brio-btn brio-btn-primary brio-flex brio-items-center brio-gap-xs"
                      style={{ background: 'var(--brio-green)' }}
                    >
                      ✓ Update Matrix
                    </button>
                    <button 
                      onClick={() => {
                        setIsEditingMatrix(false);
                        setTempSalaryMatrix(null);
                        setForm({});
                      }}
                      className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                    >
                      <X className="w-4 h-4"/>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            <BrioFullScreenButton isFullScreen={isFullScreen} onToggle={toggleFullScreen} style={{ alignSelf: 'flex-start' }} />
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          {isEditingMatrix && (
            <div style={{ background: 'var(--brio-yellow-light)', border: '1px solid var(--brio-yellow)', padding: '16px' }}>
              <p className="brio-font-medium" style={{ color: 'var(--brio-yellow)' }}>
                ⚠️ <strong>Edit Mode Active:</strong> Make your changes, then click "Update Matrix" to save or "Cancel" to discard changes.
                {needsConversion && (
                  <span style={{ marginLeft: '8px', fontWeight: 400 }}>
                    (Values are entered and stored in {baseCurrency}. Display shows {displayCurrency} at rate {getFxRateForDate(salaryFxDate)}.)
                  </span>
                )}
              </p>
            </div>
          )}

          <div style={{ background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold)', padding: '16px' }}>
            <p className="brio-analytics-body">
              💡 <strong>How it works:</strong> Set monthly salaries for each job role and level. Internal cost rates (which include bonuses and markup) are automatically calculated and shown in the Team Members section.
            </p>
          </div>

          <BrioAnalyticsSurface padding="m">
            <div style={{ overflowX: 'auto' }}>
              <BrioByCode code={PRESENTATION_CODE}>
                <BrioTable {...tableProps}>
                  <thead>
                    <tr>
                      <th style={{ position: 'sticky', left: 0, background: 'var(--brio-grey-light)', zIndex: 10 }}>Job Role</th>
                      {Array.from({ length: 21 }, (_, i) => (
                        <th key={i} className="text-center" style={{ minWidth: '60px', fontSize: '11px', padding: '4px 2px' }}>
                          L{i}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {jobRoles.map((jobRole, roleIdx) => (
                      <tr key={jobRole} className="brio-row--hoverable">
                        <td 
                          className="brio-font-medium" 
                          style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 10, borderRight: '1px solid var(--brio-border)' }}
                        >
                          {jobRole}
                          {['Support', 'Model Maker', 'Visualiser', 'Others'].includes(jobRole) && <span className="brio-text-xs brio-text-grey" style={{ marginLeft: '8px' }}>(Non-billable)</span>}
                        </td>
                        {Array.from({ length: 21 }, (_, level) => {
                          const currentMatrix = isEditingMatrix ? tempSalaryMatrix : salaryMatrix;
                          const data = currentMatrix[jobRole]?.[level] || { salary: 0, markup: 0 };
                          
                          return (
                            <td key={level} style={{ minWidth: '60px', padding: '2px' }}>
                              {isEditingMatrix ? (
                                /* ── EDIT MODE ──
                                   Input always in baseCurrency (INR).
                                   Raw numbers go straight to state — no FX on write. */
                                <input
                                  type="number"
                                  value={data.salary || ''}
                                  onChange={(e) => {
                                    setTempSalaryMatrix(prev => {
                                      const roleData = prev[jobRole] || Array.from({ length: 21 }, () => ({ salary: 0, markup: 0 }));
                                      const updated = Array.isArray(roleData)
                                        ? roleData.map((item, idx) => idx === level ? { ...item, salary: parseFloat(e.target.value) || 0 } : item)
                                        : Object.fromEntries(
                                            Object.keys(roleData).map(k => [k, parseInt(k) === level ? { ...roleData[k], salary: parseFloat(e.target.value) || 0 } : roleData[k]])
                                          );
                                      return { ...prev, [jobRole]: updated };
                                    });
                                  }}
                                  className="brio-btn brio-btn-secondary"
                                  style={{
                                    width: '100%',
                                    textAlign: 'center',
                                    padding: '2px',
                                    fontSize: '10px',
                                    background: '#fff',
                                    cursor: 'text'
                                  }}
                                  placeholder="0"
                                  title={`Monthly Salary in ${baseCurrency}`}
                                />
                              ) : (
                                /* ── VIEW MODE ──
                                   Display value converted via money() → formatMoneyInDisplayCurrency */
                                <div
                                  className="brio-btn"
                                  style={{
                                    width: '100%',
                                    textAlign: 'center',
                                    padding: '2px',
                                    background: 'var(--brio-grey-light)',
                                    cursor: 'default',
                                    fontSize: '10px',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={
                                    needsConversion && data.salary
                                      ? `Stored: ${formatMoney(data.salary, baseCurrency)} → ${displayCurrency}`
                                      : undefined
                                  }
                                >
                                  {data.salary ? money(data.salary) : '–'}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </BrioTable>
              </BrioByCode>
            </div>

            <div className="brio-mt-s" style={{ background: 'var(--brio-grey-light)', padding: '12px' }}>
              <p className="brio-font-medium brio-text-xs brio-mb-xs">Note:</p>
              <p className="brio-text-xs brio-text-grey">• Each cell shows the <strong>Full-Time Monthly Salary</strong> (176h/month) for that job role and level</p>
              <p className="brio-text-xs brio-text-grey brio-mt-xs">• Part-time employees with the same role/level have the same hourly rate; their actual monthly cost is prorated by contracted hours</p>
              <p className="brio-text-xs brio-text-grey brio-mt-xs">• Markup is calculated office-wide in the Accounting page and applies to all employees equally</p>
              <p className="brio-text-xs brio-text-grey">• Internal cost rate includes salary + bonuses and is shown in the Team Members section above</p>
              {needsConversion && (
                <p className="brio-text-xs brio-mt-xs" style={{ color: 'var(--brio-gold-hover)' }}>
                  • Values stored in {baseCurrency}; displayed in {displayCurrency} (rate: {getFxRateForDate(salaryFxDate)})
                </p>
              )}
            </div>
          </BrioAnalyticsSurface>

          {/* ── MATRIX VERSION HISTORY ── */}
          <BrioAnalyticsSurface padding="m">
            {/* Section header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <button
                onClick={() => setHistoryExpanded(!historyExpanded)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px', padding: 0,
                  fontWeight: 600, fontSize: '14px',
                  color: 'var(--brio-gold, #9F902A)',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}
              >
                {historyExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                Matrix Version History
                <span style={{ fontWeight: 400, fontSize: '12px', textTransform: 'none', letterSpacing: 0, color: 'var(--brio-muted, #94a3b8)' }}>
                  ({salaryMatrixHistory.length} {salaryMatrixHistory.length === 1 ? 'version' : 'versions'})
                </span>
              </button>
            </div>

            {historyExpanded && (
              <div style={{ marginTop: '12px' }}>
                {supabaseSalaryMatrix.loading && (
                  <div style={{ fontSize: '12px', color: 'var(--brio-muted)' }}>Loading version history...</div>
                )}

                {!supabaseSalaryMatrix.loading && salaryMatrixHistory.length > 0 && (
                  <table style={MH_STYLES.table}>
                    <thead>
                      <tr>
                        <th style={MH_STYLES.th}>#</th>
                        <th style={MH_STYLES.th}>Start Date</th>
                        <th style={MH_STYLES.th}>End Date</th>
                        <th style={MH_STYLES.th}>Status</th>
                        <th style={MH_STYLES.th}>Changed By</th>
                        <th style={MH_STYLES.th}>Reason</th>
                        <th style={MH_STYLES.th}>Created</th>
                        <th style={{ ...MH_STYLES.th, textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salaryMatrixHistory.map((revision, idx) => {
                        const today = new Date().toISOString().split('T')[0];
                        const isCurrentPeriod = revision.effectiveDate <= today && (!revision.endDate || revision.endDate >= today);
                        return (
                          <tr key={revision.id} style={{ background: isCurrentPeriod ? '#fafaf6' : 'transparent' }}>
                            <td style={MH_STYLES.td}>{salaryMatrixHistory.length - idx}</td>
                            <td style={MH_STYLES.td}>{revision.effectiveDate}</td>
                            <td style={MH_STYLES.td}>{revision.endDate || <span style={{ color: 'var(--brio-muted)', fontStyle: 'italic' }}>open</span>}</td>
                            <td style={MH_STYLES.td}>
                              {isCurrentPeriod
                                ? <span style={MH_STYLES.badge('green')}>Active</span>
                                : revision.effectiveDate > today
                                  ? <span style={MH_STYLES.badge('amber')}>Upcoming</span>
                                  : <span style={MH_STYLES.badge('default')}>Superseded</span>
                              }
                            </td>
                            <td style={MH_STYLES.td}>{revision.changedBy || '—'}</td>
                            <td style={MH_STYLES.td}>{revision.changeReason || '—'}</td>
                            <td style={MH_STYLES.td}>
                              {revision.createdAt
                                ? new Date(revision.createdAt).toLocaleDateString()
                                : '—'
                              }
                            </td>
                            <td style={{ ...MH_STYLES.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {confirmDeleteId === revision.id ? (
                                <span style={{ fontSize: '11px' }}>
                                  Delete?{' '}
                                  <button
                                    onClick={() => handleRevisionDelete(revision.id)}
                                    disabled={saving}
                                    style={{ color: '#dc2626', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px' }}
                                  >
                                    Yes
                                  </button>
                                  {' / '}
                                  <button
                                    onClick={() => setConfirmDeleteId(null)}
                                    style={{ color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px' }}
                                  >
                                    No
                                  </button>
                                </span>
                              ) : (
                                <>
                                  <button
                                    onClick={() => openRevisionEditForm(revision)}
                                    title="Edit version"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--brio-gold, #9F902A)' }}
                                  >
                                    <Pencil size={13} />
                                  </button>
                                  <button
                                    onClick={() => setConfirmDeleteId(revision.id)}
                                    title="Delete version"
                                    disabled={isCurrentPeriod && salaryMatrixHistory.length === 1}
                                    style={{
                                      background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                                      color: isCurrentPeriod && salaryMatrixHistory.length === 1 ? '#d1d5db' : '#dc2626',
                                    }}
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {!supabaseSalaryMatrix.loading && salaryMatrixHistory.length === 0 && (
                  <div style={{ fontSize: '12px', color: 'var(--brio-muted, #94a3b8)', fontStyle: 'italic', padding: '4px 0' }}>
                    No version history yet. Create a matrix revision using "Edit Matrix" above.
                  </div>
                )}

                {/* Inline edit form */}
                {showEditForm && (
                  <div style={{
                    marginTop: '8px',
                    padding: '12px',
                    background: '#fafaf6',
                    border: '1px solid var(--brio-border, #e2e8f0)',
                    borderRadius: '8px',
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--brio-text, #1e293b)' }}>
                      Edit Version
                    </div>

                    {editFormError && (
                      <div style={{
                        background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
                        padding: '8px 12px', fontSize: '12px', color: '#991b1b', marginBottom: '8px',
                      }}>
                        {editFormError}
                      </div>
                    )}

                    <div style={{ ...MH_STYLES.formGrid, gridTemplateColumns: '1fr 1fr 1fr' }}>
                      <div style={{ ...MH_STYLES.formField }}>
                        <label style={{ ...MH_STYLES.formLabel, marginBottom: '2px' }}>Start Date *</label>
                        <input
                          type="date"
                          value={editFormData.effectiveDate || ''}
                          onChange={e => setEditFormData({ ...editFormData, effectiveDate: e.target.value })}
                          style={{ fontFamily: 'Raleway, sans-serif', fontSize: '10px', padding: '3px 4px', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 0, background: 'var(--brio-white, #fff)', width: '100px', WebkitAppearance: 'none', appearance: 'none' }}
                        />
                      </div>
                      <div style={{ ...MH_STYLES.formField }}>
                        <label style={{ ...MH_STYLES.formLabel, marginBottom: '2px' }}>End Date</label>
                        <input
                          type="date"
                          value={editFormData.endDate || ''}
                          onChange={e => setEditFormData({ ...editFormData, endDate: e.target.value })}
                          style={{ fontFamily: 'Raleway, sans-serif', fontSize: '10px', padding: '3px 4px', border: '1px solid var(--brio-border, #d4d0c8)', borderRadius: 0, background: 'var(--brio-white, #fff)', width: '100px', WebkitAppearance: 'none', appearance: 'none' }}
                        />
                      </div>
                      <div style={MH_STYLES.formField}>
                        <label style={MH_STYLES.formLabel}>Change Reason</label>
                        <input
                          type="text"
                          value={editFormData.changeReason || ''}
                          onChange={e => setEditFormData({ ...editFormData, changeReason: e.target.value })}
                          placeholder="e.g., Annual revision"
                          style={MH_STYLES.inputStyle}
                        />
                      </div>
                    </div>

                    {/* ── Matrix editing grid ── */}
                    {editFormData.matrix && (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--brio-text, #1e293b)' }}>
                          Edit Salary Matrix
                        </div>
                        {needsConversion && (
                          <p style={{ fontSize: '11px', color: 'var(--brio-gold-hover)', marginBottom: '6px' }}>
                            Values are entered and stored in {baseCurrency}. Display shows {displayCurrency} at rate {getFxRateForDate(salaryFxDate)}.
                          </p>
                        )}
                        <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto', border: '1px solid var(--brio-border, #e2e8f0)', borderRadius: '6px' }}>
                          <table style={{ ...MH_STYLES.table, marginTop: 0, fontSize: '11px' }}>
                            <thead>
                              <tr>
                                <th style={{ ...MH_STYLES.th, position: 'sticky', left: 0, background: '#f8fafc', zIndex: 10, minWidth: '120px' }}>Job Role</th>
                                {Array.from({ length: 21 }, (_, i) => (
                                  <th key={i} style={{ ...MH_STYLES.th, textAlign: 'center', minWidth: '80px' }}>L{i}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {jobRoles.map((jobRole) => (
                                <tr key={jobRole}>
                                  <td style={{
                                    ...MH_STYLES.td, position: 'sticky', left: 0, background: '#fff', zIndex: 10,
                                    borderRight: '1px solid var(--brio-border, #e2e8f0)', fontWeight: 500, whiteSpace: 'nowrap',
                                  }}>
                                    {jobRole}
                                  </td>
                                  {Array.from({ length: 21 }, (_, level) => {
                                    const data = editFormData.matrix[jobRole]?.[level] || { salary: 0, markup: 0 };
                                    return (
                                      <td key={level} style={{ ...MH_STYLES.td, padding: '2px 3px' }}>
                                        <input
                                          type="number"
                                          value={data.salary || ''}
                                          onChange={(e) => {
                                            setEditFormData(prev => {
                                              const newMatrix = { ...prev.matrix };
                                              const roleData = newMatrix[jobRole] || {};
                                              const updatedRole = Array.isArray(roleData)
                                                ? roleData.map((item, idx) => idx === level ? { ...item, salary: parseFloat(e.target.value) || 0 } : item)
                                                : { ...roleData, [level]: { ...(roleData[level] || { salary: 0, markup: 0 }), salary: parseFloat(e.target.value) || 0 } };
                                              newMatrix[jobRole] = updatedRole;
                                              return { ...prev, matrix: newMatrix };
                                            });
                                          }}
                                          placeholder="0"
                                          title={`${jobRole} L${level} — Monthly Salary in ${baseCurrency}`}
                                          style={{
                                            width: '100%', textAlign: 'center', padding: '4px 2px',
                                            border: '1px solid var(--brio-border, #d1d5db)', borderRadius: '3px',
                                            fontSize: '11px', boxSizing: 'border-box',
                                          }}
                                        />
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={resetEditForm}
                        disabled={saving}
                        style={{
                          fontSize: '12px', padding: '4px 12px', borderRadius: '4px',
                          background: '#fff', border: '1px solid var(--brio-border, #d1d5db)',
                          color: 'var(--brio-text, #475569)', cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRevisionSave}
                        disabled={saving}
                        style={{
                          fontSize: '12px', padding: '4px 12px', borderRadius: '4px',
                          background: 'var(--brio-gold, #9F902A)', border: 'none', color: '#fff',
                          cursor: 'pointer', fontWeight: 600,
                          opacity: saving ? 0.6 : 1,
                        }}
                      >
                        {saving ? 'Saving...' : 'Update'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </BrioAnalyticsSurface>
        </div>
      </BrioFrame>
    </div>
  );
}
